/**
 * Debug utilities for tracking API calls in the exam system
 */

// Tracks all API calls made during the session
const apiCallTracker = {
  calls: [],
  answers: {
    successful: [],
    failed: []
  },
  
  // Add a new API call to the tracker
  trackCall(url, method, success, data, error = null, duration = 0) {
    const call = {
      timestamp: new Date().toISOString(),
      url,
      method,
      success,
      duration,
      data: data ? (typeof data === 'object' ? JSON.stringify(data) : data) : null,
      error: error ? {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      } : null
    };
    
    this.calls.push(call);
    
    // Track answer-related calls separately
    if (url.includes('answer') || url.includes('answers')) {
      if (success) {
        this.answers.successful.push(call);
      } else {
        this.answers.failed.push(call);
      }
    }
    
    // Keep logs manageable by limiting the number of entries
    if (this.calls.length > 100) {
      this.calls = this.calls.slice(-100);
    }
    
    return call;
  },
  
  // Get all calls related to answers
  getAnswerCalls() {
    return this.calls.filter(call => 
      call.url.includes('answer') || call.url.includes('answers')
    );
  },
  
  // Get all failed calls
  getFailedCalls() {
    return this.calls.filter(call => !call.success);
  },
  
  // Reset the tracker
  reset() {
    this.calls = [];
    this.answers.successful = [];
    this.answers.failed = [];
  },
  
  // Print a summary of API calls to the console
  printSummary() {
    console.group('API Call Summary');
    console.log(`Total calls: ${this.calls.length}`);
    console.log(`Successful calls: ${this.calls.filter(c => c.success).length}`);
    console.log(`Failed calls: ${this.calls.filter(c => !c.success).length}`);
    console.log(`Answer-related calls: ${this.getAnswerCalls().length}`);
    console.log(`Successful answer calls: ${this.answers.successful.length}`);
    console.log(`Failed answer calls: ${this.answers.failed.length}`);
    console.groupEnd();
    
    if (this.answers.failed.length > 0) {
      console.group('Failed Answer API Calls');
      this.answers.failed.forEach((call, index) => {
        console.log(`[${index + 1}] ${call.url} (${call.method}): ${call.error?.message || 'Unknown error'}`);
      });
      console.groupEnd();
    }
  }
};

// Debug wrapper for examining API calls
const debugExamApi = {
  // Attach to existing API calls
  wrapApiCalls(examApi) {
    const originalFollowHateoasLink = examApi.followHateoasLink;
    
    // Override the followHateoasLink function to add tracking
    examApi.followHateoasLink = async (link, method = 'GET', data = null) => {
      const startTime = Date.now();
      
      try {
        const result = await originalFollowHateoasLink(link, method, data);
        const duration = Date.now() - startTime;
        
        // Track successful call
        apiCallTracker.trackCall(link, method, true, result, null, duration);
        
        return result;
      } catch (error) {
        const duration = Date.now() - startTime;
        
        // Track failed call
        apiCallTracker.trackCall(link, method, false, data, error, duration);
        
        throw error;
      }
    };
    
    // Track answer attempts specifically to help debug that feature
    examApi.debugTrackAnswerAttempt = (questionId, success, data, error = null) => {
      const url = `/api/question/${questionId}/answers`;
      apiCallTracker.trackCall(url, 'GET', success, data, error);
    };
    
    // Return the enhanced API
    return examApi;
  },
  
  // Get access to the tracker
  getTracker() {
    return apiCallTracker;
  }
};

export default debugExamApi;
